/* Everything in this file runs inside an IIFE so nothing it declares
   (helpers, event handlers, the modal functions, etc.) leaks into the
   page's shared global scope, where a same-named const/let/function
   from another section or app could collide with — or in the case of
   duplicate top-level const/let, outright break — this one. Only
   openModal/closeModal need to stay reachable from the inline
   onclick="" handlers in the markup, so those two are exposed under
   window.AfnanShopWin instead of as bare globals. */
(function(){
/* Only the stickers on the first screen stay inlined, so the hero paints
   without a round-trip. Everything below the fold is a real file that
   lazy-loads and caches between visits. */
/* AFNAN_SHOP_WIN_STICKERS is rendered by the section as a Liquid-built map of
   {key: asset_url}, so Shopify's flat, CDN-hosted /assets/ folder is the
   single source of truth for every path here — nothing hardcodes a
   relative folder the way the standalone build did. The four hero
   stickers carry data-eager in the markup (CDN assets don't benefit
   from base64-inlining the way a same-origin relative file did, so
   eager+high-priority load is the equivalent optimisation here). */
document.querySelectorAll('[data-asw-img]').forEach(i=>{
  const k=i.dataset.aswImg;
  const eager=i.hasAttribute('data-asw-eager');
  /* Decorative art must fail invisibly, never as a broken-image icon. */
  i.addEventListener('error',()=>{const w=i.closest('.asw-stk');(w||i).style.display='none';});
  i.src=(window.AFNAN_SHOP_WIN_STICKERS&&window.AFNAN_SHOP_WIN_STICKERS[k])||'';
  if(!eager){i.loading='lazy';i.decoding='async';}
});
/* ===== helpers ===== */
const REDUCE=matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ===== HERO / BOARDING PASS =====
   The hero used to be scroll-scrubbed: a 320vh sticky stage whose title
   scale, pass transform, sky opacity and stamp were all recomputed from
   scrollY on every frame. It is two ordinary static sections now, so all
   that math is gone - the pass reveals itself once via the same
   IntersectionObserver every other section already uses. */
const hero=document.getElementById('asw-hero'),bpass=document.getElementById('asw-bpass');

/* Whatever the theme's header does - sits transparently over the hero, or
   pushes it down as a normal opaque bar - this section can't know which
   without reading the theme's own header.liquid. Rather than guess the
   theme's transparent-header trigger and hope it fires, this measures the
   REAL outcome directly: how far #asw-hero actually sits from the top
   of the page at rest. If the header overlays (transparent worked), that
   gap is ~0 and nothing changes. If it doesn't, the gap equals the
   header's real height, and --asw-header-h feeds that back into the CSS so
   the first screen only claims the space actually visible below it. */
function measureHeaderGap(){
  const gap=Math.max(0,Math.round(hero.getBoundingClientRect().top+scrollY));
  document.documentElement.style.setProperty('--asw-header-h',gap+'px');
  /* The top scrim only earns its place if the header is actually floating
     over the hero (gap near 0 despite the header still being visually
     present) - if the header pushed our content down instead, it already
     has its own opaque background and a second dark band under it is just
     clutter with nothing to do. */
  document.getElementById('asw-root').classList.toggle('asw-header-overlaid',gap<40);
  measureOverlayHeader();
}

/* An overlaid header reserves no space by definition, so the gap above
   tells us nothing about how tall it actually is - and the hero's centred
   logotype can end up underneath a floating nav. This finds the header
   element itself and reports where its bottom edge falls, so the hero can
   reserve exactly that much. Best-effort by design: the selector list
   covers the usual Shopify conventions, and if none of them match, the CSS
   falls back to a viewport-scaled guess (see --asw-overlay-h in the CSS).
   Only measured at rest - a sticky header that shrinks or hides on scroll
   would otherwise report a height the hero never actually has to clear. */
function measureOverlayHeader(){
  if(scrollY>4)return;
  const el=document.querySelector('#shopify-section-header,header[role="banner"],.shopify-section-header,header,.site-header');
  if(!el)return;
  /* At scrollY 0 the viewport-relative bottom IS the document position, for
     a static and a fixed header alike - which is why this only runs at
     rest. It also picks up an announcement bar sitting above the header,
     as long as the header element itself starts below it. */
  const bottom=Math.round(el.getBoundingClientRect().bottom);
  if(bottom>20&&bottom<400)document.documentElement.style.setProperty('--asw-overlay-h',bottom+'px');
}
measureHeaderGap();
addEventListener('resize',measureHeaderGap);
/* The first measurement above can run before the header's real height has
   settled (webfonts swapping in, the logo image finishing its own load),
   so it's re-taken once those are done - otherwise the hero stays sized to
   a stale, taller header measurement forever, since nothing but resize
   re-triggers it. */
addEventListener('load',measureHeaderGap);
if(document.fonts&&document.fonts.ready){document.fonts.ready.then(measureHeaderGap);}

/* ===== pass 3D tilt ===== */
if(matchMedia('(pointer:fine)').matches&&!REDUCE){
  bpass.addEventListener('mousemove',e=>{
    const r=bpass.getBoundingClientRect();
    const px=(e.clientX-r.left)/r.width-.5, py=(e.clientY-r.top)/r.height-.5;
    bpass.style.transform=`rotateY(${px*9}deg) rotateX(${py*-7}deg)`;
  });
  bpass.addEventListener('mouseleave',()=>{bpass.style.transform='rotateY(0) rotateX(0)';});
}

/* ===== split-flap countdown (reference flip) ===== */
const target=new Date(window.AFNAN_SHOP_WIN_CAMPAIGN_TARGET).getTime();
function setFlaps(id,value){
  const digits=document.querySelectorAll('#'+id+' .asw-flap');
  const str=String(Math.min(99,value)).padStart(2,'0');
  digits.forEach((d,i)=>{
    if(d.textContent!==str[i]){
      d.classList.remove('asw-flip');void d.offsetWidth;d.classList.add('asw-flip');
      setTimeout(()=>{d.textContent=str[i];},160);
    }
  });
}
function tick(){
  const diff=Math.max(0,target-Date.now());
  setFlaps('asw-fd-d',Math.floor(diff/864e5));
  setFlaps('asw-fd-h',Math.floor(diff%864e5/36e5));
  setFlaps('asw-fd-m',Math.floor(diff%36e5/6e4));
  setFlaps('asw-fd-s',Math.floor(diff%6e4/1e3));
}
tick();setInterval(tick,1000);

/* ===== intersection triggers ===== */
const io=new IntersectionObserver(es=>es.forEach(e=>{
  if(!e.isIntersecting)return;
  e.target.classList.add('asw-on');
  io.unobserve(e.target);
}),{threshold:.15});
/* asw-bpass is in here for the "Entered" stamp, which CSS now keys off
   .asw-on with a delay instead of a scroll threshold. */
['asw-fare','asw-bpass','asw-luggage'].forEach(id=>io.observe(document.getElementById(id)));
document.querySelectorAll('.asw-paste,.asw-driftL,.asw-driftR,.asw-rise').forEach(el=>{
  io.observe(el);
});


/* ===== accordion: one open ===== */
document.querySelectorAll('#asw-acc details').forEach(d=>{
  d.addEventListener('toggle',()=>{
    if(d.open)document.querySelectorAll('#asw-acc details').forEach(o=>{if(o!==d)o.open=false;});
  });
});

/* ===== modal ===== */
function openModal(){document.getElementById('asw-overlay').classList.add('asw-open');document.body.style.overflow='hidden';}
function closeModal(){document.getElementById('asw-overlay').classList.remove('asw-open');document.body.style.overflow='';}
addEventListener('keydown',e=>{if(e.key==='Escape')closeModal();});

window.AfnanShopWin = window.AfnanShopWin || {};
window.AfnanShopWin.openModal = openModal;
window.AfnanShopWin.closeModal = closeModal;
})();
